// react
import { MouseEvent, useContext, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import {
	Account,
	Category,
	Component,
	ErrorCause,
	ExpenseType,
	Loan,
	Transaction,
	TransactionType,
} from '../../../@types';
import { Size, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces';

// context
import { ContextBanner, ContextUser } from '../../../App.context';

// mui
import { Alert, Grid } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// pages
import { ContextSoeTransactions } from '../../pages/Project/Reports/Soe/Soe.context';
import { ContextProject, ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// ui
import { TableInfoParams } from '../../ui/TableInfoParams/TableInfoParams';

// icons
import {
	ArticleOutlined as IconArticle,
	InsertDriveFileOutlined as IconInsertDriveFile,
} from '@mui/icons-material';

// utils
import { formatDate } from '../../../utils/dates';
import { downloadFile, jsonToCsv } from '../../../utils/files';
import { formatNumber } from '../../../utils/format';
import { groupTransactions, getGroupDateHeaders, transactionGroupToRows } from '../../../utils/soe';

// fields
import { Input } from '../../fields/Input/Input';
import { RadioButtons } from '../../fields/RadioButtons/RadioButtons';

// props
export interface DialogExportSoeReportSummaryProps {
	accounts: Account[];
	categories: Category[];
	components: Component[];
	expenseTypes: ExpenseType[];
	filtersValues: Record<string, string | string[]>;
	isOpen: boolean;
	loans: Loan[];
	onClose: () => void;
	onSuccess?: () => void;
}

export const DialogExportSoeReportSummary = ({
	accounts,
	categories,
	components,
	expenseTypes,
	filtersValues,
	isOpen,
	loans,
	onClose,
	onSuccess,
}: DialogExportSoeReportSummaryProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [error, setError] = useState<{ message: string; errors?: string[] } | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [searchParams] = useSearchParams();

	// context
	const { transactionsGrouping, columns } = useContext(ContextSoeTransactions);
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramId = searchParams.get('id');
	const paramReadOnlyFields = searchParams.get('readOnlyFields')?.split(',');
	const paramWithdrawalApplicationNew = searchParams.get('withdrawalApplicationNew');

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// vars
	const groupKeys = columns.filter((c) => c.checked).map((col) => col.key);
	const idForm = 'form-export-soe-report-summary';
	const fromAccount = accounts.find((acc) => acc.id === Number(filtersValues.fromAccount));
	const loan = loans.find((loan) => loan.id === Number(filtersValues.loan));

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// clear error
		setError(null);

		// set loading
		setIsLoading(true);

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const dateToday = formatDate({ date: new Date(), format: 'YYYY-MM-DD' });
		let fileName = `${project?.idWbg}-${loan?.code}-${dateToday}-soe-summary`;

		// set pdf options
		const pdfOptions: Omit<TDocumentDefinitions, 'content'> = {
			pageOrientation: 'landscape',
			pageSize: 'LETTER',
		};

		try {
			// construct fetch transactions url
			let urlFetchTransactions = `${process.env.REACT_APP_API_URL}/transactions`;
			// add id param
			if (paramId) {
				urlFetchTransactions += `?id=${paramId}`;
			} else {
				urlFetchTransactions += `?`;
			}
			// define filters keys
			const filtersKeys = Object.keys(filtersValues);

			// define withdrawal applicationId id
			let withdrawalApplicationId: string | number = '';

			// add each filter value to url
			for (let i = 0; i < filtersKeys.length; i++) {
				const key = filtersKeys[i];

				if (key === 'fromAccount') {
					urlFetchTransactions += `&or=${encodeURIComponent(
						`fromAccount:=:${filtersValues[key]}`
					)},${encodeURIComponent(`designatedAccount:=:${filtersValues[key]}`)}`;
				} else if (Array.isArray(filtersValues[key])) {
					if (filtersValues[key].length) {
						urlFetchTransactions += `&${key}=${(filtersValues[key] as string[]).join(',')}`;
					}
				} else if (filtersValues[key]) {
					urlFetchTransactions += `&${key}=${filtersValues[key]}`;
				}
			}

			// fetch transaction ids
			const fetchGetTransactionIds = await fetch(`${urlFetchTransactions}&fields=transaction.id`, {
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});

			const resGetTransactionIds = await fetchGetTransactionIds.json();

			if (resGetTransactionIds.error) {
				throw new Error(resGetTransactionIds.error.message, {
					cause: {
						id: resGetTransactionIds.error.id,
					},
				});
			}

			if (entries['withdrawalApplication']) {
				// update file name with withdrawalApplication id
				fileName = `${project?.idWbg}-${loan?.code}-${entries['withdrawalApplication']}-${dateToday}-soe-summary`;

				const getWithdrawalApplication = await fetch(
					`${process.env.REACT_APP_API_URL}/withdrawal-applications?idExternal=${String(
						entries['withdrawalApplication']
					)}&project=${project?.id}`,
					{
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					}
				);

				const resGetWithdrawalApplication = await getWithdrawalApplication.json();

				if (resGetWithdrawalApplication.error) {
					throw new Error(resGetWithdrawalApplication.error.message, {
						cause: {
							id: resGetWithdrawalApplication.error.id,
						},
					});
				}
				if (resGetWithdrawalApplication.data.length) {
					withdrawalApplicationId = resGetWithdrawalApplication.data[0].id;
				} else {
					const postWithdrawalApplication = await fetch(
						`${process.env.REACT_APP_API_URL}/withdrawal-applications/`,
						{
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
								Authorization: `Bearer ${accessToken}`,
								User: String(idToken),
							},
							body: JSON.stringify({
								idExternal: String(entries['withdrawalApplication']),
								project: project?.id,
								user: user?.id ? user?.id : undefined,
							}),
						}
					);

					const resPostWithdrawalApplication = await postWithdrawalApplication.json();

					if (resPostWithdrawalApplication.data) {
						withdrawalApplicationId = resPostWithdrawalApplication.data.id;
					}

					if (resPostWithdrawalApplication.error) {
						throw new Error(resPostWithdrawalApplication.error.message, {
							cause: {
								id: resPostWithdrawalApplication.error.id,
							},
						});
					}
				}
				const reqUpdateTransaction = await fetch(`${process.env.REACT_APP_API_URL}/transactions`, {
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(
						resGetTransactionIds.data.map((tx: Transaction) => {
							return {
								id: tx.id,
								withdrawalApplication: withdrawalApplicationId,
							};
						})
					),
				});
				const resUpdateTransactions = await reqUpdateTransaction.json();

				if (resUpdateTransactions.error) {
					throw new Error(resUpdateTransactions.error.message, {
						cause: {
							id: resUpdateTransactions.error.id,
						},
					});
				}

				if (resUpdateTransactions.data.failed.length) {
					throw new Error(
						`${resUpdateTransactions.data.failed.length} transactions failed to update.`,
						{
							cause: {
								id: 'TRANSACTION_PATCH_INCOMPLETE',
								res: resUpdateTransactions,
							},
						}
					);
				}
			}

			// format transactions for csv
			const { monthHeaders } = getGroupDateHeaders({ transactionsGrouping });

			// generate grouped data
			const nodes = transactionsGrouping?.length
				? groupTransactions({
						currency: fromAccount?.currency,
						groupKeys,
						monthHeaders,
						t,
						transactionsGrouping,
					})
				: [];

			const rows = transactionGroupToRows({ groupKeys, monthHeaders, nodes, t });

			// Build header information (used by both PDF and CSV)
			if (!withdrawalApplicationId && entries['withdrawalApplication']) {
				withdrawalApplicationId = entries['withdrawalApplication'] as string;
			}
			const headerInfo = {
				title: `${t('soe')} - ${t('summary')}`,
				subtitle: `${t('paymentsMadeDuringPeriod')} ${t('from')}: ${
					filtersValues.dateStart
						? formatDate({
								date: new Date(filtersValues.dateStart as string),
								format: 'YYYY-MM-DD',
							})
						: t('na')
				} ${t('to')}: ${
					filtersValues.dateEnd
						? formatDate({
								date: new Date(filtersValues.dateEnd as string),
								format: 'YYYY-MM-DD',
							})
						: t('na')
				}`,
				stats: [
					{
						label: t('project'),
						value: `${project?.name || ''} (${project?.idWbg || ''})`,
					},
					{ label: t('loanNo'), value: loan?.code || t('na') },
					{
						label: t('applicationNo'),
						value: entries['withdrawalApplication'] || withdrawalApplicationId || t('na'),
					},
					{ label: t('designatedAccount'), value: fromAccount?.name || t('na') },
				],
			};

			const rowsFormatted = rows.map((row) => {
				const rowFormatted: Record<string, string | number | null | undefined> = {};
				Object.entries(row).forEach(([key, value]) => {
					if (typeof value === 'number') {
						rowFormatted[key] = formatNumber(value, {
							maximumFractionDigits: 2,
							minimumFractionDigits: 2,
							useGrouping: entries['format'] === 'pdf',
						});
					} else {
						rowFormatted[key] = value as string | number | null | undefined;
					}
				});
				return rowFormatted;
			});

			if (entries['format'] === 'pdf') {
				// convert rows data to proper table format for PDF
				const tableBody: TableCell[][] = [];
				const widths: Size[] = [];

				if (rowsFormatted.length) {
					// Create headers row - just strings, the backend will apply styling
					const headers = Object.keys(rowsFormatted[0]);
					tableBody.push(headers);

					// Format all rows as strings
					rowsFormatted.forEach((row) => {
						const dataRow = headers.map((header) => {
							const value = row[header];
							return value != null ? String(value) : '';
						});
						tableBody.push(dataRow);
					});

					// Set widths for table columns - using proper pdfmake Size types
					headers.forEach((key) => {
						let width: Size = 'auto';
						if ([t('component'), t('category'), t('expenseType')].includes(key)) {
							width = '*';
						}
						widths.push(width);
					});
				}

				const reqPostPdf = await fetch(`${process.env.REACT_APP_API_URL}/services/pdfs`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						options: pdfOptions,
						header: headerInfo,
						tables: [
							{
								maxCols: 19,
								table: {
									body: tableBody,
									widths,
								},
							},
						],
						title: t('soe'),
					}),
				});
				const resPostPdf = await reqPostPdf.json();
				if (resPostPdf.error) {
					throw new Error(resPostPdf.error.message, {
						cause: {
							id: resPostPdf.error.id,
						},
					});
				}

				// download as pdf file
				downloadFile({
					data: new Uint8Array(resPostPdf?.data?.blob?.data).buffer,
					name: `${fileName}.pdf`,
					type: 'application/pdf',
				});
			} else if (entries['format'] === 'csv') {
				// Build CSV header
				const headerLines = [
					headerInfo.title,
					headerInfo.subtitle,
					...(headerInfo.stats?.map(
						(item) => `${item.label}: ${String(item.value).replace(/,/g, '')}`
					) || []),
					'', // blank line
				];

				const csv = headerLines.join('\n') + '\n' + jsonToCsv({ array: rowsFormatted });

				// download as csv file
				downloadFile({
					data: csv,
					name: `${fileName}.csv`,
					type: 'text/csv',
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.reportDownloaded'),
				severity: 'success',
			});

			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				if (cause.id === 'WITHDRAWAL_APPLICATION_ID_DUPLICATE') {
					message = t('error.withdrawalApplicationIdDuplicate', {
						idExternal: entries['withdrawalApplication'],
					});
				}

				if (cause.id === 'TRANSACTION_PATCH_INCOMPLETE') {
					message = t('error.transactionsPatchIncomplete', {
						failed: cause.res.data.failed.length,
					});
				}
			}

			// set error
			setError({
				message,
			});
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('download'),
					form: idForm,
					loading: isLoading,
					type: 'submit',
					variant: 'contained',
				},
			]}
			hasClose={true}
			isOpen={isOpen}
			onClose={onClose}
			title={{
				children: t('exportSoe'),
				variant: 'h3',
			}}>
			<Grid item xs={12}>
				<form id={idForm} name="formExportSoeReport" onSubmit={handleSubmit}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TableInfoParams
								amountMax={filtersValues.amountMax as string}
								amountMin={filtersValues.amountMin as string}
								categories={categories.filter((c) =>
									filtersValues.category.includes(c.id.toString())
								)}
								components={components.filter((c) =>
									filtersValues.component.includes(c.id.toString())
								)}
								currency={filtersValues.currency as string}
								dateEnd={filtersValues.dateEnd as string}
								dateStart={filtersValues.dateStart as string}
								expenseTypes={expenseTypes.filter((e) =>
									filtersValues.expenseType.includes(e.id.toString())
								)}
								fromAccount={fromAccount}
								groupBy={groupKeys}
								loan={loan}
								types={filtersValues.type as TransactionType[]}
							/>
						</Grid>
						{userProjectPermissions?.withdrawalApplication?.create && (
							<>
								<Grid item xs={12}>
									<Input
										id={`${idForm}-withdrawal-application`}
										label={t('withdrawalApplication')}
										name="withdrawalApplication"
										defaultValue={paramWithdrawalApplicationNew || ''}
										InputProps={{
											inputProps: {
												pattern: "^[a-zA-Z0-9\\(\\)'\\/\\.,:?+\\-]*$",
												title:
													'Only alphanumeric characters and ( ) / . , : ? + - special characters are allowed without spaces.',
											},
											readOnly: paramReadOnlyFields?.includes('withdrawalApplication') || false,
										}}
										maxLength={20}
										helperText={t(`borrowerReference#`)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Alert severity="info">{t('alert.existingWithdrawalApplications')}</Alert>
								</Grid>
								<Grid item xs={12}>
									<RadioButtons
										defaultValue={'csv'}
										id={`${idForm}-export-file-format`}
										inputs={[
											{
												icon: <IconArticle />,
												label: t('csv'),
												value: 'csv',
											},
											{
												icon: <IconInsertDriveFile />,
												label: t('pdf'),
												value: 'pdf',
											},
										]}
										label={t('format')}
										name="format"
										required={true}
									/>
								</Grid>
							</>
						)}
						{error && (
							<Grid item xs={12}>
								<Alert severity="error">{error.message}</Alert>
							</Grid>
						)}
					</Grid>
				</form>
			</Grid>
		</DialogActions>
	);
};
